// Tracks the most-recently visited route titles. Capped, deduped (most-recent
// occurrence kept), with a 30-day TTL so stale entries fall off.

import { useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import * as store from './localStore';

const KEY = 'recently-viewed:v1';
const MAX = 6;
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const EVENT = 'tn:recently-viewed-changed';

const SKIP_ROUTES = new Set(['/', '/404']);

const read = () => {
  const list = store.get(KEY, []);
  return Array.isArray(list) ? list : [];
};

const writeAndNotify = (next) => {
  store.set(KEY, next, { ttlMs: TTL_MS });
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVENT));
};

export const recordView = (path, title) => {
  if (!path || SKIP_ROUTES.has(path)) return;
  const current = read();
  const filtered = current.filter((entry) => entry.path !== path);
  const next = [{ path, title: title || path, viewedAt: Date.now() }, ...filtered].slice(0, MAX);
  writeAndNotify(next);
};

const subscribe = (cb) => {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(EVENT, cb);
  window.addEventListener('storage', cb);
  return () => {
    window.removeEventListener(EVENT, cb);
    window.removeEventListener('storage', cb);
  };
};

export const useRecentlyViewed = () => useSyncExternalStore(subscribe, read, () => []);

// Drop-in hook that records the current route. Mount once near the root.
export const useRecordRoute = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    // Defer one tick so the page has set its <title> first.
    const id = setTimeout(() => {
      const docTitle = typeof document !== 'undefined' ? document.title : '';
      recordView(pathname, docTitle);
    }, 250);
    return () => clearTimeout(id);
  }, [pathname]);
};
